/**
 * Collection of all Vega-Lite Error Messages
 */
import { stringValue } from 'vega';
import { getSizeChannel } from '../channel';
import { stringify } from '../util';
export function invalidSpec(spec) {
  return "Invalid specification ".concat(stringify(spec), ". Make sure the specification includes at least one of the following properties: \"mark\", \"layer\", \"facet\", \"hconcat\", \"vconcat\", \"concat\", or \"repeat\".");
}
// FIT
export var FIT_NON_SINGLE = 'Autosize "fit" only works for single views and layered views.';
export function containerSizeNonSingle(name) {
  var uName = name == 'width' ? 'Width' : 'Height';
  return "".concat(uName, " \"container\" only works for single views and layered views.");
}
export function containerSizeNotCompatibleWithAutosize(name) {
  var uName = name == 'width' ? 'Width' : 'Height';
  var fitDirection = name == 'width' ? 'x' : 'y';
  return "".concat(uName, " \"container\" only works well with autosize \"fit\" or \"fit-").concat(fitDirection, "\".");
}
export function droppingFit(channel) {
  return channel ? "Dropping \"fit-".concat(channel, "\" because spec has discrete ").concat(getSizeChannel(channel), ".") : "Dropping \"fit\" because spec has discrete size.";
}
// VIEW SIZE
export function unknownField(channel) {
  return "Unknown field for ".concat(channel, ". Cannot calculate view size.");
}
// SELECTION
export function cannotProjectOnChannelWithoutField(channel) {
  return "Cannot project a selection on encoding channel \"".concat(channel, "\", which has no field.");
}
export function cannotProjectAggregate(channel, aggregate) {
  return "Cannot project a selection on encoding channel \"".concat(channel, "\" as it uses an aggregate function (\"").concat(aggregate, "\").");
}
export function nearestNotSupportForContinuous(mark) {
  return "The \"nearest\" transform is not supported for ".concat(mark, " marks.");
}
export function selectionNotSupported(mark) {
  return "Selection not supported for ".concat(mark, " yet.");
}
export function selectionNotFound(name) {
  return "Cannot find a selection named \"".concat(name, "\".");
}
export var SCALE_BINDINGS_CONTINUOUS = 'Scale bindings are currently only supported for scales with unbinned, continuous domains.';
export var SEQUENTIAL_SCALE_DEPRECATED = 'Sequntial scales are deprecated. The available quantitative scale type values are linear, log, pow, sqrt, symlog, time and utc';
export var LEGEND_BINDINGS_MUST_HAVE_PROJECTION = 'Legend bindings are only supported for selections over an individual field or encoding channel.';
export function cannotLookupVariableParameter(name) {
  return "Lookups can only be performed on selection parameters. \"".concat(name, "\" is a variable parameter.");
}
export function noSameUnitLookup(name) {
  return "Cannot define and lookup the \"".concat(name, "\" selection in the same view. ") + "Try moving the lookup into a second, layered view?";
}
export var NEEDS_SAME_SELECTION = 'The same selection must be used to override scale domains in a layered view.';
export var INTERVAL_INITIALIZED_WITH_POS = 'Interval selections should be initialized using "x", "y", "longitude", or "latitude" keys.';
// REPEAT
export function noSuchRepeatedValue(field) {
  return "Unknown repeated value \"".concat(field, "\".");
}
export function columnsNotSupportByRowCol(type) {
  return "The \"columns\" property cannot be used when \"".concat(type, "\" has nested row/column.");
}
export var MULTIPLE_TIMER_ANIMATION_SELECTION = 'Multiple timer selections in one unit spec are not supported. Ignoring all but the first.';
export var MULTI_VIEW_ANIMATION_UNSUPPORTED = 'Animation involving facet, layer, or concat is currently unsupported.';
export function selectionAsScaleDomainWithoutField(field) {
  return 'A "field" or "encoding" must be specified when using a selection as a scale domain. ' + "Using \"field\": ".concat(stringValue(field), ".");
}
export function selectionAsScaleDomainWrongEncodings(encodings, encoding, extent, field) {
  return (!encodings.length ? 'No ' : 'Multiple ') + "matching ".concat(stringValue(encoding), " encoding found for selection ").concat(stringValue(extent.param), ". ") + "Using \"field\": ".concat(stringValue(field), ".");
}
// CONCAT / REPEAT
export var CONCAT_CANNOT_SHARE_AXIS = 'Axes cannot be shared in concatenated or repeated views yet (https://github.com/vega/vega-lite/issues/2415).';
// DATA
export function unrecognizedParse(p) {
  return "Unrecognized parse \"".concat(p, "\".");
}
export function differentParse(field, local, ancestor) {
  return "An ancestor parsed field \"".concat(field, "\" as ").concat(ancestor, " but a child wants to parse the field as ").concat(local, ".");
}
export var ADD_SAME_CHILD_TWICE = 'Attempt to add the same child twice.';
// TRANSFORMS
export function invalidTransformIgnored(transform) {
  return "Ignoring an invalid transform: ".concat(stringify(transform), ".");
}
export var NO_FIELDS_NEEDS_AS = 'If "from.fields" is not specified, "as" has to be a string that specifies the key to be used for the data from the secondary source.';
// ENCODING & FACET
export function customFormatTypeNotAllowed(channel) {
  return "Config.customFormatTypes is not true, thus custom format type and format for channel ".concat(channel, " are dropped.");
}
export function projectionOverridden(opt) {
  var parentProjection = opt.parentProjection,
    projection = opt.projection;
  return "Layer's shared projection ".concat(stringify(parentProjection), " is overridden by a child projection ").concat(stringify(projection), ".");
}
export var REPLACE_ANGLE_WITH_THETA = 'Arc marks uses theta channel rather than angle, replacing angle with theta.';
export function offsetNestedInsideContinuousPositionScaleDropped(mainChannel) {
  return "".concat(mainChannel, "Offset dropped because ").concat(mainChannel, " is continuous");
}
export function primitiveChannelDef(channel, type, value) {
  return "Channel ".concat(channel, " is a ").concat(type, ". Converted to {value: ").concat(stringify(value), "}.");
}
export function invalidFieldType(type) {
  return "Invalid field type \"".concat(type, "\".");
}
export function invalidFieldTypeForCountAggregate(type, aggregate) {
  return "Invalid field type \"".concat(type, "\" for aggregate: \"").concat(aggregate, "\", using \"quantitative\" instead.");
}
export function invalidAggregate(aggregate) {
  return "Invalid aggregation operator \"".concat(aggregate, "\".");
}
export function missingFieldType(channel, newType) {
  return "Missing type for channel \"".concat(channel, "\", using \"").concat(newType, "\" instead.");
}
export function droppingColor(type, opt) {
  var fill = opt.fill,
    stroke = opt.stroke;
  return "Dropping color ".concat(type, " as the plot also has ").concat(fill && stroke ? 'fill and stroke' : fill ? 'fill' : 'stroke', ".");
}
export function relativeBandSizeNotSupported(sizeChannel) {
  return "Position range does not support relative band size for ".concat(sizeChannel, ".");
}
export function emptyFieldDef(fieldDef, channel) {
  return "Dropping ".concat(stringify(fieldDef), " from channel \"").concat(channel, "\" since it does not contain any data field, datum, value, or signal.");
}
export var LINE_WITH_VARYING_SIZE = 'Line marks cannot encode size with a non-groupby field. You may want to use trail marks instead.';
export function incompatibleChannel(channel, markOrFacet, when) {
  return "".concat(channel, " dropped as it is incompatible with \"").concat(markOrFacet, "\"").concat(when ? " when ".concat(when) : '', ".");
}
export function offsetEncodingScaleIgnored(channel) {
  return "".concat(channel, " encoding has no scale, so specified scale is ignored.");
}
export function invalidEncodingChannel(channel) {
  return "".concat(channel, "-encoding is dropped as ").concat(channel, " is not a valid encoding channel.");
}
export function channelShouldBeDiscrete(channel) {
  return "".concat(channel, " encoding should be discrete (ordinal / nominal / binned).");
}
export function channelShouldBeDiscreteOrDiscretizing(channel) {
  return "".concat(channel, " encoding should be discrete (ordinal / nominal / binned) or use a discretizing scale (e.g. threshold).");
}
export function facetChannelDropped(channels) {
  return "Facet encoding dropped as ".concat(channels.join(' and '), " ").concat(channels.length > 1 ? 'are' : 'is', " also specified.");
}
export function discreteChannelCannotEncode(channel, type) {
  return "Using discrete channel \"".concat(channel, "\" to encode \"").concat(type, "\" field can be misleading as it does not encode ").concat(type === 'ordinal' ? 'order' : 'magnitude', ".");
}
// MARK
export function rangeMarkAlignmentCannotBeExpression(align) {
  return "The ".concat(align, " for range marks cannot be an expression");
}
export function lineWithRange(hasX2, hasY2) {
  var channels = hasX2 && hasY2 ? 'x2 and y2' : hasX2 ? 'x2' : 'y2';
  return "Line mark is for continuous lines and thus cannot be used with ".concat(channels, ". We will use the rule mark (line segments) instead.");
}
export function orientOverridden(original, actual) {
  return "Specified orient \"".concat(original, "\" overridden with \"").concat(actual, "\".");
}
// SCALE
export var CANNOT_UNION_CUSTOM_DOMAIN_WITH_FIELD_DOMAIN = 'Custom domain scale cannot be unioned with default field-based domain.';
export function cannotUseScalePropertyWithNonColor(prop) {
  return "Cannot use the scale property \"".concat(prop, "\" with non-color channel.");
}
export function cannotUseRelativeBandSizeWithNonBandScale(scaleType) {
  return "Cannot use the relative band size with ".concat(scaleType, " scale.");
}
export function unaggregateDomainHasNoEffectForRawField(fieldDef) {
  return "Using unaggregated domain with raw field has no effect (".concat(stringify(fieldDef), ").");
}
export function unaggregateDomainWithNonSharedDomainOp(aggregate) {
  return "Unaggregated domain not applicable for \"".concat(aggregate, "\" since it produces values outside the origin domain of the source data.");
}
export function unaggregatedDomainWithLogScale(fieldDef) {
  return "Unaggregated domain is currently unsupported for log scale (".concat(stringify(fieldDef), ").");
}
export function cannotApplySizeToNonOrientedMark(mark) {
  return "Cannot apply size to non-oriented mark \"".concat(mark, "\".");
}
export function scaleTypeNotWorkWithChannel(channel, scaleType, defaultScaleType) {
  return "Channel \"".concat(channel, "\" does not work with \"").concat(scaleType, "\" scale. We are using \"").concat(defaultScaleType, "\" scale instead.");
}
export function scaleTypeNotWorkWithFieldDef(scaleType, defaultScaleType) {
  return "FieldDef does not work with \"".concat(scaleType, "\" scale. We are using \"").concat(defaultScaleType, "\" scale instead.");
}
export function scalePropertyNotWorkWithScaleType(scaleType, propName, channel) {
  return "".concat(channel, "-scale's \"").concat(propName, "\" is dropped as it does not work with ").concat(scaleType, " scale.");
}
export function scaleTypeNotWorkWithMark(mark, scaleType) {
  return "Scale type \"".concat(scaleType, "\" does not work with mark \"").concat(mark, "\".");
}
export function stepDropped(channel) {
  return "The step for \"".concat(channel, "\" is dropped because the ").concat(channel === 'width' ? 'x' : 'y', " is continuous.");
}
export function mergeConflictingProperty(property, propertyOf, v1, v2) {
  return "Conflicting ".concat(propertyOf.toString(), " property \"").concat(property.toString(), "\" (").concat(stringify(v1), " and ").concat(stringify(v2), "). Using ").concat(stringify(v1), ".");
}
export function mergeConflictingDomainProperty(property, propertyOf, v1, v2) {
  return "Conflicting ".concat(propertyOf.toString(), " property \"").concat(property.toString(), "\" (").concat(stringify(v1), " and ").concat(stringify(v2), "). Using the union of the two domains.");
}
export function independentScaleMeansIndependentGuide(channel) {
  return "Setting the scale to be independent for \"".concat(channel, "\" means we also have to set the guide (axis or legend) to be independent.");
}
export function domainSortDropped(sort) {
  return "Dropping sort property ".concat(stringify(sort), " as unioned domains only support boolean or op \"count\", \"min\", and \"max\".");
}
export var MORE_THAN_ONE_SORT = 'Domains that should be unioned has conflicting sort properties. Sort will be set to true.';
export var FACETED_INDEPENDENT_DIFFERENT_SOURCES = 'Detected faceted independent scales that union domain of multiple fields from different data sources. We will use the first field. The result view size may be incorrect.';
export var FACETED_INDEPENDENT_SAME_FIELDS_DIFFERENT_SOURCES = 'Detected faceted independent scales that union domain of the same fields from different source. We will assume that this is the same field from a different fork of the same data source. However, if this is not the case, the result view size may be incorrect.';
export var FACETED_INDEPENDENT_SAME_SOURCE = 'Detected faceted independent scales that union domain of multiple fields from the same data source. We will use the first field. The result view size may be incorrect.';
// AXIS
export var INVALID_CHANNEL_FOR_AXIS = 'Invalid channel for axis.';
// STACK
export function cannotStackRangedMark(channel) {
  return "Cannot stack \"".concat(channel, "\" if there is already \"").concat(channel, "2\".");
}
export function stackNonLinearScale(scaleType) {
  return "Stack is applied to a non-linear scale (".concat(scaleType, ").");
}
export function stackNonSummativeAggregate(aggregate) {
  return "Stacking is applied even though the aggregate function is non-summative (\"".concat(aggregate, "\").");
}
// TIMEUNIT
export function invalidTimeUnit(unitName, value) {
  return "Invalid ".concat(unitName, ": ").concat(stringify(value), ".");
}
export function droppedDay(d) {
  return "Dropping day from datetime ".concat(stringify(d), " as day cannot be combined with other units.");
}
export function errorBarCenterAndExtentAreNotNeeded(center, extent) {
  return "".concat(extent ? 'extent ' : '').concat(extent && center ? 'and ' : '').concat(center ? 'center ' : '').concat(extent && center ? 'are ' : 'is ', "not needed when data are aggregated.");
}
export function errorBarCenterIsUsedWithWrongExtent(center, extent, mark) {
  return "".concat(center, " is not usually used with ").concat(extent, " for ").concat(mark, ".");
}
export function errorBarContinuousAxisHasCustomizedAggregate(aggregate, compositeMark) {
  return "Continuous axis should not have customized aggregation function ".concat(aggregate, "; ").concat(compositeMark, " already agregates the axis.");
}
export function errorBand1DNotSupport(property) {
  return "1D error band does not support ".concat(property, ".");
}
// CHANNEL
export function channelRequiredForBinned(channel) {
  return "Channel ".concat(channel, " is required for \"binned\" bin.");
}
export function channelShouldNotBeUsedForBinned(channel) {
  return "Channel ".concat(channel, " should not be used with \"binned\" bin.");
}
export function domainRequiredForThresholdScale(channel) {
  return "Domain for ".concat(channel, " is required for threshold scale.");
}